import { z } from "zod";
import { Created, Id, IsoDate, IsoDateTime, OptionalText } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

/** Mirrors core/modules/appointments/domain/appointment.ts. */
export const APPOINTMENT_STATUSES = ["booked", "done", "cancelled", "no_show"] as const;
export const AppointmentStatus = z.enum(APPOINTMENT_STATUSES);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

/** The clinic's slot lengths (domain rule: anything else is `appointment_duration_invalid`). */
export const APPOINTMENT_DURATIONS = [30, 45, 60, 90] as const;
export const DurationMin = z.union([z.literal(30), z.literal(45), z.literal(60), z.literal(90)]);

export const Specialist = z.object({ id: Id, name: z.string() });
export type Specialist = z.infer<typeof Specialist>;

/** An agenda row (week view / dashboard) — carries patient chrome. */
export const AgendaItem = z.object({
  id: Id,
  patientId: Id,
  patientName: z.string(),
  episodeId: Id.nullable(),
  startsAt: IsoDateTime,
  durationMin: z.number().int(),
  status: AppointmentStatus,
  comment: z.string().nullable(),
  /** Set once the booking was captured — lets day surfaces show billing state. */
  visitId: Id.nullable(),
  specialistId: Id.nullable(),
  specialistName: z.string().nullable(),
});
export type AgendaItem = z.infer<typeof AgendaItem>;

/** One column of the week view. `iso` is the clinic-local date (TZ=Europe/Kyiv). */
export const ScheduleDay = z.object({
  iso: IsoDate,
  dayOfMonth: z.number().int(),
  isToday: z.boolean(),
  isWeekend: z.boolean(),
  items: z.array(AgendaItem),
  /** Booked+done minutes — the ambient load bar. */
  workedMin: z.number().int(),
  /** `workedMin` against an 8-hour working day, 0–100. */
  loadPct: z.number().int(),
});
export type ScheduleDay = z.infer<typeof ScheduleDay>;

/** The week view read model: everything /schedule renders, assembled once. */
export const ScheduleWeekView = z.object({
  /** The clock at assembly time — the «зараз» divider, past/upcoming split. */
  now: IsoDateTime,
  todayISO: IsoDate,
  /** Clinic-local dates: the shown week's Monday, the Monday after (→ link), the Monday before (← link). */
  monday: IsoDate,
  nextMonday: IsoDate,
  prevMonday: IsoDate,
  days: z.array(ScheduleDay),
  /** Flat agenda of the week (the header count). */
  items: z.array(AgendaItem),
  specialists: z.array(Specialist),
  /** The specialist filter, only when it names a real specialist. */
  activeSpecialistId: Id.optional(),
  /** A booked weekend works full-width — narrow tickets clip the row menu. */
  busyWeekend: z.boolean(),
});
export type ScheduleWeekView = z.infer<typeof ScheduleWeekView>;

/** What the booking form needs before it renders. */
export const BookingContext = z.object({
  specialists: z.array(Specialist),
  /** The `specialistId` query param, only when it names a real specialist. */
  requestedSpecialistId: Id.optional(),
  /** The `date` query param, only when it is a `YYYY-MM-DD` date. */
  defaultDate: IsoDate.optional(),
  /** Role-aware preselection: a specialist lands in their own form; others follow the filter. */
  defaultSpecialistId: Id.optional(),
});
export type BookingContext = z.infer<typeof BookingContext>;

export const AppointmentSnapshot = z.object({
  id: Id,
  patientId: Id,
  episodeId: Id.nullable(),
  specialistId: Id.nullable(),
  startsAt: IsoDateTime,
  durationMin: z.number().int(),
  status: AppointmentStatus,
  visitId: Id.nullable(),
  comment: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AppointmentSnapshot = z.infer<typeof AppointmentSnapshot>;

/** A mutation's before/after — what the audit diff is built from and what a client can show. */
export const AppointmentChange = z.object({
  before: AppointmentSnapshot,
  after: AppointmentSnapshot,
});
export type AppointmentChange = z.infer<typeof AppointmentChange>;

export const BookAppointmentBody = z.object({
  patientId: Id,
  episodeId: Id.nullable().optional(),
  /**
   * Whose schedule this lands on. Omitted: a specialist books onto their own; a manager/admin
   * books onto the PATIENT's specialist (they have no schedule of their own).
   */
  specialistId: Id.nullable().optional(),
  startsAt: IsoDateTime,
  durationMin: DurationMin.default(60),
  comment: OptionalText(500),
});
export type BookAppointmentBody = z.infer<typeof BookAppointmentBody>;

export const UpdateAppointmentBody = z.object({
  startsAt: IsoDateTime,
  durationMin: DurationMin.optional(),
  comment: OptionalText(500),
});
export type UpdateAppointmentBody = z.infer<typeof UpdateAppointmentBody>;

export const APPOINTMENT_TRANSITIONS = ["cancel", "no_show", "rebook"] as const;
export const TransitionAppointmentBody = z.object({ transition: z.enum(APPOINTMENT_TRANSITIONS) });
export type TransitionAppointmentBody = z.infer<typeof TransitionAppointmentBody>;

export const appointments = defineRoutes({
  scheduleWeek: {
    method: "GET",
    path: "/schedule/week",
    summary: "The week grid: 7 days of agenda, load, navigation anchors and the specialist filter",
    query: z.object({
      /** Any date inside the wanted week; junk or absent → the current week. */
      week: z.string().max(32).optional(),
      /** Narrows the agenda only when it names a real specialist. */
      specialistId: Id.optional(),
    }),
    responses: { 200: ScheduleWeekView, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  bookingContext: {
    method: "GET",
    path: "/schedule/booking-context",
    summary: "Specialists + validated preselection for the booking form",
    query: z.object({ specialistId: Id.optional(), date: z.string().max(32).optional() }),
    responses: { 200: BookingContext, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  book: {
    method: "POST",
    path: "/appointments",
    summary: "Book a session (same-specialist overlap is a hard 409)",
    body: BookAppointmentBody,
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      409: PROBLEMS[409],
      422: PROBLEMS[422],
    },
  },
  update: {
    method: "PATCH",
    path: "/appointments/:appointmentId",
    summary: "Move / re-annotate a booked session; the −24h reminder follows",
    params: z.object({ appointmentId: Id }),
    body: UpdateAppointmentBody,
    responses: {
      200: AppointmentChange,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      409: PROBLEMS[409],
      422: PROBLEMS[422],
    },
  },
  transition: {
    method: "POST",
    path: "/appointments/:appointmentId/transition",
    summary: "cancel | no_show | rebook (rebook re-checks the overlap rule)",
    params: z.object({ appointmentId: Id }),
    body: TransitionAppointmentBody,
    responses: {
      200: AppointmentChange,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      409: PROBLEMS[409],
      422: PROBLEMS[422],
    },
  },
});
